import React from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Paper,
} from "@mui/material";
import { CloudUpload, Delete, AudioFile, PictureAsPdf } from "@mui/icons-material";

interface MediaUploadSectionProps {
  type: string;
  selectedMedia: File | null;
  mediaPreview: string | null;
  uploadProgress: number;
  error?: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
  type,
  selectedMedia,
  mediaPreview,
  uploadProgress,
  error,
  fileInputRef,
  onFileSelect,
  onRemove,
}) => {
  const getAcceptType = () => {
    if (type === "video") return "video/*";
    if (type === "audio") return "audio/*";
    if (type === "pdf") return "application/pdf";
    return "";
  };

  const getFormats = () => {
    if (type === "video") return "MP4, WebM, MOV";
    if (type === "audio") return "MP3, WAV, OGG";
    if (type === "pdf") return "PDF";
    return "";
  };

  const getTypeLabel = () => {
    if (type === "video") return "Video";
    if (type === "audio") return "Audio";
    if (type === "pdf") return "PDF";
    return "";
  };

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant='subtitle2'
          sx={{ mb: 0.5, fontWeight: 500, color: "text.secondary" }}
        >
          {getTypeLabel()} Upload
        </Typography>
        <Typography
          variant='caption'
          sx={{
            display: "block",
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          Supported formats: <strong>{getFormats()}</strong> • Max size:{" "}
          <strong>100MB</strong>
        </Typography>
      </Box>

      {mediaPreview ? (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 2,
            bgcolor: "grey.50",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant='body2' fontWeight={500}>
                {selectedMedia
                  ? selectedMedia.name
                  : `Existing ${type}`}
              </Typography>
              {selectedMedia && (
                <Typography variant='caption' color='text.secondary'>
                  {(selectedMedia.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              )}
              {!selectedMedia && mediaPreview && type === "pdf" && (
                <Typography variant='caption' color='text.secondary'>
                  Click to view PDF
                </Typography>
              )}
            </Box>
            <IconButton onClick={onRemove} color='error' size='small'>
              <Delete />
            </IconButton>
          </Box>

          {/* Media Preview */}
          {type === "video" ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 400,
                margin: "0 auto",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "black",
                aspectRatio: "16/9",
              }}
            >
              <video
                src={mediaPreview}
                controls
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  console.error("Video preview error:", e);
                }}
              >
                Your browser does not support the video tag.
              </video>
            </Box>
          ) : type === "audio" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                bgcolor: "primary.light",
                borderRadius: 2,
              }}
            >
              <AudioFile sx={{ fontSize: 40, color: "primary.main" }} />
              <Box sx={{ flex: 1 }}>
                <audio src={mediaPreview} controls style={{ width: "100%" }}>
                  Your browser does not support the audio tag.
                </audio>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  bgcolor: "error.light",
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <PictureAsPdf sx={{ fontSize: 40, color: "error.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body2' fontWeight={500}>
                    {selectedMedia ? selectedMedia.name : "PDF file attached"}
                  </Typography>
                  {selectedMedia && (
                    <Typography variant='caption' color='text.secondary'>
                      {(selectedMedia.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  height: "400px",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "grey.100",
                  position: "relative",
                }}
              >
                <iframe
                  src={`${mediaPreview}#toolbar=1`}
                  title="PDF Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  onError={(e) => {
                    console.error("PDF preview error:", e);
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                >
                  <Button
                    variant='contained'
                    size='small'
                    href={mediaPreview}
                    target='_blank'
                    rel='noopener noreferrer'
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      color: "text.primary",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  >
                    Open in New Tab
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      ) : null}

      <input
        ref={fileInputRef}
        type='file'
        accept={getAcceptType()}
        onChange={onFileSelect}
        style={{ display: "none" }}
        id='media-upload-input'
      />
      <label htmlFor='media-upload-input'>
        <Button
          component='span'
          variant='outlined'
          startIcon={<CloudUpload />}
          fullWidth
          sx={{
            borderRadius: 2,
            py: 2,
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: error ? "error.main" : "primary.main",
            "&:hover": {
              borderStyle: "dashed",
              borderWidth: 2,
              bgcolor: "action.hover",
            },
          }}
        >
          {mediaPreview
            ? `Replace ${getTypeLabel()}`
            : `Upload ${getTypeLabel()} File`}
        </Button>
      </label>

      {error && (
        <Typography
          variant='caption'
          color='error'
          sx={{ mt: 0.5, display: "block" }}
        >
          {error}
        </Typography>
      )}

      {uploadProgress > 0 && uploadProgress < 100 && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant='caption' color='text.secondary'>
              Uploading {type}...
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {Math.round(uploadProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={uploadProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: "grey.200",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default MediaUploadSection;

